<script setup lang="ts">
import type { ObjectId, RadioConfig } from '~/store/radios'

import { useI18n } from 'vue-i18n'
import { useStore } from '~/store'
import { computed } from 'vue'

import Button from '~/ui/Button.vue'

interface Props {
  customRadioId?: number | null
  type?: string
  clientOnly?: boolean
  objectId?: ObjectId | number | string | null
  radioConfig?: RadioConfig | null
  playOnly?: boolean
  lowHeight?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  customRadioId: null,
  type: '',
  clientOnly: false,
  objectId: null,
  radioConfig: null,
  playOnly: false,
  lowHeight: false
})

const store = useStore()
const effectiveType = computed(() => props.customRadioId ? 'custom' : props.type)

const running = computed(() => {
  if (!store.state.radios.running) {
    return false
  }

  const { current } = store.state.radios
  return current?.type === effectiveType.value
    && current?.customRadioId === props.customRadioId
    && (
      (typeof props.objectId === 'object' && current.objectId?.fullUsername === props.objectId?.fullUsername)
        || current.objectId === props.objectId
    )
})

const { t } = useI18n()
const buttonLabel = computed(() => {
  switch (props.type) {
    case 'tag':
      return running.value
        ? t('components.radios.Button.stopTagsRadio')
        : t('components.radios.Button.startTagsRadio')
    case 'artist':
      return running.value
        ? t('components.radios.Button.stopArtistsRadio')
        : t('components.radios.Button.startArtistsRadio')
    case 'playlist':
      return running.value
        ? t('components.radios.Button.stopPlaylistsRadio')
        : t('components.radios.Button.startPlaylistsRadio')
    case 'actor-content':
      return running.value
        ? t('components.radios.Button.stopActorRadio')
        : t('components.radios.Button.startActorRadio')
    case 'actor-activity':
      return running.value
        ? t('components.radios.Button.stopActorActivityRadio')
        : t('components.radios.Button.startActorActivityRadio')
    default:
      return running.value
        ? t('components.radios.Button.stopRadio')
        : t('components.radios.Button.startRadio')
  }
})
const tooltipLabel = computed(() => {
  switch (props.type) {
    case 'tag':
      return t('components.radios.Button.tooltipTagsRadio')
    case 'artist':
            return t('components.radios.Button.tooltipArtistsRadio')
    case 'playlist':
          return t('components.radios.Button.tooltipPlaylistsRadio')
    case 'actor-activity':
           return t('components.radios.Button.tooltipActorActivityRadio')
    case 'actor-content':
           return t('components.radios.Button.tooltipActorRadio')
    default:
            return t('components.radios.Button.tooltipRadio')
  }
})


const toggleRadio = () => {
  if (running.value) {
    return store.dispatch('radios/stop')
  }

  return store.dispatch('radios/start', {
    type: effectiveType.value,
    objectId: props.objectId,
    customRadioId: props.customRadioId,
    clientOnly: props.clientOnly,
    config: props.radioConfig,
    playOnly: props.playOnly
  })
}
</script>

<template>
  <tooltip :content="tooltipLabel">
    <Button
      :aria-pressed="running"
      :class="`color-primary-solid shape-${store.state.auth.authenticated && playOnly ? 'circle' : 'field-minimal'}`"
      @click="toggleRadio"
      :ui-block-size="lowHeight ? '40' : undefined"
    >
      <Icon
        :icon="running ? 'bi:pause-fill' : 'bi:play-fill'"
        style="font-size: 2em;"
      />
      <div v-if="!playOnly">
        {{ buttonLabel }}
      </div>
    </Button>
  </tooltip>
</template>
